import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
type Tab = 'experience' | 'resources' | 'fun';
export function About() {
  const [activeTab, setActiveTab] = useState<Tab>('experience');
  const tabs: {
    id: Tab;
    label: string;
  }[] = [{
    id: 'experience',
    label: 'EXPERIENCE'
  }, {
    id: 'resources',
    label: 'RESOURCES'
  }, {
    id: 'fun',
    label: 'FUN PROJECTS'
  }];
  return <div className="min-h-screen bg-[#fdf9e6]" data-id="element-24">
      {/* Tab Navigation */}
      <div className="max-w-4xl mx-auto px-6 sm:px-10 pt-12" data-id="element-25">
        <nav className="flex justify-center gap-10 sm:gap-16" data-id="element-26">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`
                text-xs sm:text-sm tracking-[0.2em] font-medium pb-2 transition-colors relative
                ${activeTab === tab.id ? 'text-blue-600' : 'text-[#0a0e2e]/40 hover:text-[#0a0e2e]/70'}
              `} data-id="element-27">
              {tab.label}
              {activeTab === tab.id && <motion.div layoutId="aboutTabIndicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-28" />}
            </button>)}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="max-w-4xl mx-auto px-6 sm:px-10 pt-16 pb-20" data-id="element-29">
        <AnimatePresence mode="wait" data-id="element-30">
          {activeTab === 'experience' && <motion.div key="experience" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3
        }} data-id="element-31">
              <ExperienceTab data-id="element-32" />
            </motion.div>}
          {activeTab === 'resources' && <motion.div key="resources" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3
        }} data-id="element-33">
              <ResourcesTab data-id="element-34" />
            </motion.div>}
          {activeTab === 'fun' && <motion.div key="fun" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.3
        }} data-id="element-35">
              <FunProjectsTab data-id="element-36" />
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}
function ExperienceTab() {
  return <div className="space-y-16" data-id="element-37">
      {/* Agency Experience */}
      <section data-id="element-38">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0a0e2e] mb-6" data-id="element-39">
          Agency Experience
        </h2>
        <p className="text-lg leading-relaxed text-[#0a0e2e]/75" data-id="element-40">
          My professional career started in advertising. I joined a staffing
          agency which gave me an opportunity to work at over a dozen companies
          in Chicago. I designed everything from Thanksgiving coupons; packaging
          for frozen fish sticks; vehicle wraps for Burger King tailgate
          parties; flash banners for JP Morgan; emails for Deutsche Bank; walkie
          talkie ads for Motorola and many more. I learned how to capture ideas
          and execute them into creative deliverables and empower our clients
          with transcending stories.
        </p>

        {/* Team photo placeholder — replace with your actual team photo */}
        <div className="mt-10 rounded-xl overflow-hidden bg-[#0a0e2e]/[0.06] aspect-[16/9] flex items-center justify-center" data-id="element-41">
          <p className="text-sm text-[#0a0e2e]/30 text-center px-8" data-id="element-42">
            Team photo — brick-wall studio with colleagues collaborating around
            a table
          </p>
        </div>
      </section>

      {/* Product Design */}
      <section data-id="element-43">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0a0e2e] mb-6" data-id="element-44">
          Product Design
        </h2>
        <p className="text-lg leading-relaxed text-[#0a0e2e]/75" data-id="element-45">
          After agency life, I transitioned into product design where I could
          focus deeply on solving complex problems over time. I've worked with
          cross-functional teams at companies like Groupon, Workato, and Testim
          — designing everything from merchant tools and automation platforms to
          testing frameworks. Each role taught me something new about balancing
          user needs with business goals.
        </p>
      </section>

      {/* Timeline */}
      <section data-id="element-46">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0a0e2e] mb-8" data-id="element-47">
          Career Timeline
        </h2>
        <div className="space-y-6 border-l-2 border-[#0a0e2e]/10 pl-8" data-id="element-48">
          {[{
          year: '2020–Present',
          role: 'Senior UX Designer',
          company: 'Workato'
        }, {
          year: '2018–2020',
          role: 'UX Designer',
          company: 'Testim'
        }, {
          year: '2015–2018',
          role: 'Product Designer',
          company: 'Groupon'
        }, {
          year: '2013–2015',
          role: 'UX/UI Designer',
          company: 'Sprout Social'
        }, {
          year: '2010–2013',
          role: 'Visual Designer',
          company: 'Agency (Various Clients)'
        }].map(item => <div key={item.year} className="relative" data-id="element-49">
              <div className="absolute -left-[2.35rem] top-1.5 w-3 h-3 rounded-full bg-[#0a0e2e]/20 border-2 border-[#fdf9e6]" data-id="element-50" />
              <p className="text-xs font-mono text-[#0a0e2e]/40 mb-1" data-id="element-51">
                {item.year}
              </p>
              <p className="font-semibold text-[#0a0e2e]" data-id="element-52">{item.role}</p>
              <p className="text-[#0a0e2e]/60" data-id="element-53">{item.company}</p>
            </div>)}
        </div>
      </section>
    </div>;
}
function ResourcesTab() {
  return <div data-id="element-54">
      <h2 className="text-2xl sm:text-3xl font-bold text-[#0a0e2e] mb-6" data-id="element-55">
        Resources
      </h2>
      <p className="text-lg leading-relaxed text-[#0a0e2e]/75 mb-10" data-id="element-56">
        Tools, books, and references I find valuable in my design practice.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-57">
        {[{
        title: "Don't Make Me Think",
        author: 'Steve Krug',
        type: 'Book'
      }, {
        title: 'The Design of Everyday Things',
        author: 'Don Norman',
        type: 'Book'
      }, {
        title: 'Figma',
        author: 'Primary design tool',
        type: 'Tool'
      }, {
        title: 'Miro',
        author: 'Whiteboarding & workshops',
        type: 'Tool'
      }, {
        title: 'Maze',
        author: 'Usability testing',
        type: 'Tool'
      }, {
        title: 'Notion',
        author: 'Documentation & specs',
        type: 'Tool'
      }].map(item => <div key={item.title} className="p-5 bg-white rounded-xl border border-[#0a0e2e]/[0.06]" data-id="element-58">
            <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-[#0a0e2e]/40" data-id="element-59">
              {item.type}
            </span>
            <h3 className="font-semibold text-[#0a0e2e] mt-1" data-id="element-60">{item.title}</h3>
            <p className="text-sm text-[#0a0e2e]/60 mt-0.5" data-id="element-61">{item.author}</p>
          </div>)}
      </div>
    </div>;
}
function FunProjectsTab() {
  return <div data-id="element-62">
      <h2 className="text-2xl sm:text-3xl font-bold text-[#0a0e2e] mb-6" data-id="element-63">
        Fun Projects
      </h2>
      <p className="text-lg leading-relaxed text-[#0a0e2e]/75 mb-10" data-id="element-64">
        Side projects and experiments outside of my day-to-day work.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-65">
        {[{
        title: 'Photography',
        description: 'Street and architectural photography around Chicago.'
      }, {
        title: 'Design Mentoring',
        description: 'Helping junior designers grow their craft and careers.'
      }].map(project => <div key={project.title} data-id="element-66">
            {/* Replace with actual project images */}
            <div className="aspect-[4/3] bg-[#0a0e2e]/[0.06] rounded-xl mb-4 flex items-center justify-center" data-id="element-67">
              <p className="text-sm text-[#0a0e2e]/30" data-id="element-68">
                {project.title} — add image
              </p>
            </div>
            <h3 className="font-semibold text-[#0a0e2e]" data-id="element-69">{project.title}</h3>
            <p className="text-sm text-[#0a0e2e]/60 mt-1" data-id="element-70">
              {project.description}
            </p>
          </div>)}
      </div>
    </div>;
}